import { collection, doc, getDoc, getDocs, onSnapshot, query, updateDoc, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { createDefaultNodeSet, type ProposalBuilderData, type ProposalNodeSet } from '../types';
import { enabledProposalStructures, type StructureRef } from './proposalStructureRefs';

export type ProposalCompareTarget = {
  id: string;
  dealId: string;
  label: string;
  subtitle: string;
  status?: string;
};

export type { StructureRef } from './proposalStructureRefs';
export { enabledProposalStructures } from './proposalStructureRefs';

export async function loadProposalStructures(dealId: string): Promise<StructureRef[]> {
  const snap = await getDoc(doc(db, 'proposalBuilders', dealId));
  if (!snap.exists()) return [];
  const data = snap.data() as ProposalBuilderData;
  return enabledProposalStructures(data.nodeSets);
}

/**
 * Live structures for a deal — a structure added to the proposal mid-session
 * shows up in Files / the upload modal without a reload. Only emits when the
 * id+name list actually changes.
 */
export function subscribeProposalStructures(
  dealId: string,
  onData: (structures: StructureRef[]) => void,
  onError?: (err: Error) => void,
): () => void {
  let lastKey: string | null = null;
  return onSnapshot(
    doc(db, 'proposalBuilders', dealId),
    snap => {
      const list = snap.exists() ? enabledProposalStructures((snap.data() as ProposalBuilderData).nodeSets) : [];
      const key = list.map(s => `${s.id}:${s.name}`).join('|');
      if (key === lastKey) return;
      lastKey = key;
      onData(list);
    },
    err => onError?.(err),
  );
}

function nodeSetsFromData(data: ProposalBuilderData | undefined): ProposalNodeSet[] {
  return data?.nodeSets || [];
}

/** Full nodeSets for structure compare (read-only). Resolves by doc id or dealId. */
export async function loadProposalNodeSets(dealOrProposalId: string): Promise<ProposalNodeSet[]> {
  const direct = await getDoc(doc(db, 'proposalBuilders', dealOrProposalId));
  if (direct.exists()) {
    const sets = nodeSetsFromData(direct.data() as ProposalBuilderData);
    if (sets.length) return sets;
  }
  const byDeal = await getDocs(
    query(collection(db, 'proposalBuilders'), where('dealId', '==', dealOrProposalId)),
  );
  if (!byDeal.empty) {
    return nodeSetsFromData(byDeal.docs[0].data() as ProposalBuilderData);
  }
  return direct.exists() ? nodeSetsFromData(direct.data() as ProposalBuilderData) : [];
}

/** Read-only list of proposals for the structure compare picker (includes sent / approved). */
export async function listProposalCompareTargets(): Promise<ProposalCompareTarget[]> {
  const snap = await getDocs(query(collection(db, 'proposalBuilders'), orderBy('updatedAt', 'desc')));
  return snap.docs.map(d => {
    const data = d.data() as ProposalBuilderData;
    const dealId = data.dealId || d.id;
    const label = data.projectName || data.clientName || data.slug || 'Untitled proposal';
    const bits = [data.clientName && data.clientName !== label ? data.clientName : '', data.slug || ''];
    return {
      id: d.id,
      dealId,
      label,
      subtitle: bits.filter(Boolean).join(' · '),
      status: data.status,
    };
  });
}

/** Append a new structure to the deal proposal and return its id + name. */
export async function appendProposalStructure(dealId: string, name: string): Promise<StructureRef> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Structure name is required.');
  const ref = doc(db, 'proposalBuilders', dealId);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? ((snap.data() as ProposalBuilderData).nodeSets ?? []) : [];
  const lastNodeSet = existing[existing.length - 1];
  let newY = 0;
  if (lastNodeSet) {
    const lastHeight = lastNodeSet.dimensions?.height || 240;
    newY = lastNodeSet.position.y + lastHeight + 50;
  }
  const newNodeSet = createDefaultNodeSet(trimmed);
  newNodeSet.position = { x: 50, y: newY };
  newNodeSet.id = `nodeset-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const nodeSets = [...existing, newNodeSet];
  if (snap.exists()) {
    await updateDoc(ref, { nodeSets, updatedAt: Timestamp.now() });
  } else {
    throw new Error('No proposal found for this deal. Create a proposal first.');
  }
  return { id: newNodeSet.id, name: newNodeSet.name };
}
